import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PageWrapper from "../../components/layout/PageWrapper";
import Badge from "../../components/ui/Badge";
import useAuth from "../../hooks/useAuth";
import { axiosPrivate } from "../../api/axios";
import chatService from "../../api/chatService";
import { isAdmin } from "../../utils/permissions";
import { getTicketDisplayId } from "../../utils/ticketUtils";
import { boardTaskService } from "../../api/taskBoardService";
import { STATUS_LABELS } from "../../components/task-board/constants";
import {
  ListTodo,
  Ticket,
  MessageSquare,
  Calendar,
  User,
  AlertCircle,
  CheckCircle2,
  Rocket,
  Eye,
  TestTube,
  FileText,
  ChevronDown,
  RefreshCw,
  Copy,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const AssignedToYou = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const employee = auth?.employee;
  const isAdminUser = isAdmin(employee);

  const [activeFilter, setActiveFilter] = useState("all");
  const [allTasks, setAllTasks] = useState([]);
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [relevantChats, setRelevantChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedTaskCode, setCopiedTaskCode] = useState(null);

  // Admin: employee selector state - initialize with current user's ID
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(() => {
    return employee?._id || employee?.id || null;
  });
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  // Fetch employees list (for admin dropdown)
  const fetchEmployees = async () => {
    if (!isAdminUser) return;

    try {
      setLoadingEmployees(true);
      const response = await axiosPrivate.get("/api/employees");
      const allEmployees = response.data || [];
      setEmployees(allEmployees);

      // Ensure selectedEmployeeId is set to current user if not already set
      if (!selectedEmployeeId && employee?._id) {
        setSelectedEmployeeId(employee._id || employee.id);
      }
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Fetch tasks assigned to the target employee from the unified task board.
  // The API applies squad visibility, so admins see every assignee's tasks.
  const fetchAssignedTasks = async () => {
    try {
      const targetEmployeeId = isAdminUser
        ? selectedEmployeeId || employee?._id || employee?.id
        : employee?._id || employee?.id;

      if (!targetEmployeeId) {
        setAllTasks([]);
        return;
      }

      const response = await boardTaskService.list({ assignee: String(targetEmployeeId) });
      setAllTasks((response.data || []).filter((task) => task.status !== "done"));
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setAllTasks([]);
    }
  };

  // Fetch assigned tickets
  const fetchAssignedTickets = async () => {
    try {
      // Determine which employee to fetch tickets for
      // For admins: use selectedEmployeeId if set, otherwise current user
      // For normal users: always use current user
      let targetEmployeeId;
      if (isAdminUser) {
        targetEmployeeId = selectedEmployeeId || employee?._id || employee?.id;
      } else {
        targetEmployeeId = employee?._id || employee?.id;
      }

      if (!targetEmployeeId) {
        setAssignedTickets([]);
        return;
      }

      const response = await axiosPrivate.get("/api/tickets");
      const ticketsData =
        response.data?.tickets || response.data?.data || response.data || [];

      // Filter tickets assigned to target employee
      const filteredTickets = Array.isArray(ticketsData)
        ? ticketsData.filter((ticket) => {
            // Check if ticket is assigned to the target employee
            const assignedToArray = ticket.assignedTo || [];
            const isAssigned = assignedToArray.some((assignee) => {
              const assigneeId = assignee?._id || assignee;
              return String(assigneeId) === String(targetEmployeeId);
            });
            return isAssigned;
          })
        : [];

      setAssignedTickets(filteredTickets);
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
      setAssignedTickets([]);
    }
  };

  // Fetch relevant chats (channels the user is a member of – shown in Assigned To You)
  const fetchRelevantChats = async () => {
    try {
      // Determine which user's channels to fetch
      let targetEmployeeId;
      if (isAdminUser) {
        targetEmployeeId = selectedEmployeeId || employee?._id || employee?.id;
      } else {
        targetEmployeeId = employee?._id || employee?.id;
      }

      if (!targetEmployeeId) {
        setRelevantChats([]);
        return;
      }

      const isViewingSelf =
        String(targetEmployeeId) === String(employee?._id || employee?.id);

      // Admin viewing another user: fetch that user's channels via admin endpoint
      const data = isViewingSelf
        ? await chatService.getChannels()
        : await chatService.getChannelsForMember(targetEmployeeId);

      const channels = data?.channels || data || [];
      const list = Array.isArray(channels)
        ? channels.map((ch) => ({
            id: ch._id || ch.id,
            _id: ch._id || ch.id,
            displayName: ch.displayName || ch.name || "Channel",
            name: ch.name,
            user: ch.displayName || ch.name || "Channel",
            timestamp: "",
          }))
        : [];
      setRelevantChats(list);
    } catch (error) {
      console.error("Failed to fetch chats:", error);
      setRelevantChats([]);
    }
  };

  // Load employees list for admin on mount
  useEffect(() => {
    if (isAdminUser) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminUser]);

  // Update selectedEmployeeId when employee changes (e.g., on login)
  useEffect(() => {
    if (employee?._id || employee?.id) {
      const currentEmployeeId = employee._id || employee.id;
      // Only update if not already set or if it's different
      if (!selectedEmployeeId || selectedEmployeeId !== currentEmployeeId) {
        setSelectedEmployeeId(currentEmployeeId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?._id, employee?.id]);

  // Load all data when employee or selectedEmployeeId changes
  useEffect(() => {
    const loadData = async () => {
      // Clear previous user's data immediately to prevent stale state leakage
      setAllTasks([]);
      setAssignedTickets([]);
      setRelevantChats([]);
      setLoading(true);
      await Promise.all([
        fetchAssignedTasks(),
        fetchAssignedTickets(),
        fetchRelevantChats(),
      ]);
      setLoading(false);
    };

    if (employee && (selectedEmployeeId || !isAdminUser)) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, selectedEmployeeId]);

  // Refetch when assignments change (real-time via Firebase notifications – not socket)
  useEffect(() => {
    const refetchAll = () => {
      fetchAssignedTasks();
      fetchAssignedTickets();
      fetchRelevantChats();
    };
    const handleTicketAssigned = () => refetchAll();
    const handleChatAssigned = () => refetchAll();
    const handleChannelCreated = () => refetchAll();
    window.addEventListener("ticket-assigned", handleTicketAssigned);
    window.addEventListener("chat-assigned", handleChatAssigned);
    window.addEventListener("chat-channel-created", handleChannelCreated);
    return () => {
      window.removeEventListener("ticket-assigned", handleTicketAssigned);
      window.removeEventListener("chat-assigned", handleChatAssigned);
      window.removeEventListener("chat-channel-created", handleChannelCreated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when page becomes visible (e.g. user returns to tab) so new tasks/tickets/chats appear
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        employee &&
        (selectedEmployeeId || !isAdminUser)
      ) {
        fetchAssignedTasks();
        fetchAssignedTickets();
        fetchRelevantChats();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, selectedEmployeeId]);

  // Filter data based on active filter
  const filteredData = useMemo(() => {
    switch (activeFilter) {
      case "tasks":
        return { type: "tasks", items: allTasks };
      case "tickets":
        return { type: "tickets", items: assignedTickets };
      case "chat":
        return { type: "chat", items: relevantChats };
      default:
        return {
          type: "all",
          items: [
            ...allTasks.map((item) => ({ ...item, itemType: "task" })),
            ...assignedTickets.map((item) => ({ ...item, itemType: "ticket" })),
            ...relevantChats.map((item) => ({ ...item, itemType: "chat" })),
          ],
        };
    }
  }, [activeFilter, allTasks, assignedTickets, relevantChats]);

  const filters = [
    {
      id: "all",
      label: "All",
      icon: ListTodo,
      count: allTasks.length + assignedTickets.length + relevantChats.length,
    },
    { id: "tasks", label: "Tasks", icon: ListTodo, count: allTasks.length },
    {
      id: "tickets",
      label: "Tickets",
      icon: Ticket,
      count: assignedTickets.length,
    },
    {
      id: "chat",
      label: "Chat",
      icon: MessageSquare,
      count: relevantChats.length,
    },
  ];

  const stripHtml = (html) => {
    if (!html) return "";
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: "bg-danger-soft text-danger",
      high: "bg-danger-soft text-danger",
      medium: "bg-warn-soft text-warn",
      low: "bg-raise text-muted",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusVariant = (status) => {
    const variants = {
      open: "danger",
      "in-progress": "warning",
      resolved: "success",
      closed: "default",
      done: "success",
    };
    return variants[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      product_backlog: FileText,
      sprint_backlog: ListTodo,
      ready: ListTodo,
      in_progress: Rocket,
      code_review: Eye,
      qa_testing: TestTube,
      qa_rejected: AlertCircle,
      done: CheckCircle2,
      open: AlertCircle,
      resolved: CheckCircle2,
      closed: CheckCircle2,
    };
    return icons[status] || FileText;
  };

  const renderTaskCard = (task) => {
    const StatusIcon = getStatusIcon(task.status);
    const taskCode = task.number ? `#${task.number}` : null;

    return (
      <motion.div
        key={`task-${task._id || task.id}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card !p-5 cursor-pointer group flex flex-col hover:-translate-y-0.5 hover:border-accent-line"
        onClick={() =>
          navigate(`/task-board?q=${encodeURIComponent(task.title)}`)
        }
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-faint" />
            <span className="font-mono text-[12px] text-faint uppercase tracking-wide">Task</span>
          </div>
          <span
            className={`px-2 py-1 rounded-md text-[11.5px] font-medium ${getPriorityColor(task.priority)}`}
          >
            {task.priority || "medium"}
          </span>
        </div>

        {/* Task Code */}
        {taskCode && (
          <div className="mb-2 flex items-center gap-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono bg-raise text-muted">
              {taskCode}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(taskCode);
                setCopiedTaskCode(taskCode);
                setTimeout(() => setCopiedTaskCode(null), 2000);
              }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-dark-100 rounded transition-colors"
              title="Copy task code"
            >
              {copiedTaskCode === taskCode ? (
                <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
              ) : (
                <Copy className="w-3 h-3 text-gray-500 dark:text-light-500" />
              )}
            </button>
          </div>
        )}

        <h3 className="font-display text-[16px] font-semibold text-ink mb-2 leading-snug">
          {task.title || "Task"}
        </h3>
        <p className="text-[13.5px] text-muted mb-4 line-clamp-2 flex-1">
          {stripHtml(task.description || "")}
        </p>

        <div className="flex items-center justify-between pt-3.5 border-t border-line text-[13px]">
          <div className="flex items-center gap-2">
            <StatusIcon className="w-4 h-4 text-gray-400 dark:text-light-500" />
            <span className="text-sm text-gray-600 dark:text-light-400 capitalize">
              {STATUS_LABELS[task.status] || task.status}
            </span>
          </div>
          {task.dueDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400 dark:text-light-500" />
              <span className="font-mono text-[12.5px] text-muted">
                {new Date(task.dueDate).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {task.labels && task.labels.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {task.labels.map((label, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-gray-100 dark:bg-dark-100 text-gray-600 dark:text-light-400 rounded-md text-xs font-medium"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </motion.div>
    );
  };

  const renderTicketCard = (ticket) => {
    return (
      <motion.div
        key={`ticket-${ticket._id || ticket.id}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card !p-5 cursor-pointer group flex flex-col hover:-translate-y-0.5 hover:border-accent-line"
        onClick={() =>
          navigate(`/communication/tickets/${ticket._id || ticket.id}`)
        }
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Ticket className="w-4 h-4 text-faint" />
            <span className="font-mono text-[12px] text-faint uppercase tracking-wide">
              Ticket # {getTicketDisplayId(ticket)}
            </span>
          </div>
          <Badge variant={getStatusVariant(ticket.status)} size="sm">
            {ticket.status}
          </Badge>
        </div>

        <h3 className="font-display text-[16px] font-semibold text-ink mb-2 leading-snug">
          {ticket.title}
        </h3>
        <p className="text-[13.5px] text-muted mb-4 line-clamp-2 flex-1">
          {ticket.description}
        </p>

        <div className="flex items-center justify-between pt-3.5 border-t border-line text-[13px]">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400 dark:text-light-500" />
            <span className="text-sm text-gray-600 dark:text-light-400">
              {ticket.createdBy?.fullNameEn || ticket.createdBy}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400 dark:text-light-500" />
            <span className="font-mono text-[12.5px] text-muted">
              {new Date(ticket.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          {ticket.category && (
            <Badge variant="warning" size="sm">
              {ticket.category}
            </Badge>
          )}
          <span
            className={`px-2 py-1 rounded-md text-[11.5px] font-medium ${getPriorityColor(ticket.priority)}`}
          >
            {ticket.priority || "medium"}
          </span>
        </div>
      </motion.div>
    );
  };

  const renderChatCard = (chat) => {
    const label = chat.user ?? chat.displayName ?? chat.name ?? "Channel";
    const chatId = chat.id ?? chat._id;
    return (
      <motion.div
        key={`chat-${chatId}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card !p-5 cursor-pointer group flex flex-col hover:-translate-y-0.5 hover:border-accent-line"
        onClick={() => navigate("/communication/chat")}
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-accent-soft text-accent-ink font-display font-semibold flex items-center justify-center text-lg flex-shrink-0">
            {chat.avatar || (label && label[0]) || "#"}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-ok" />
                <span className="text-sm font-semibold text-gray-900 dark:text-light-100">
                  {label}
                </span>
              </div>
              {chat.timestamp ? (
                <span className="text-xs text-gray-500 dark:text-light-500">
                  {chat.timestamp}
                </span>
              ) : null}
            </div>
            {chat.message ? (
              <p className="text-sm text-gray-600 dark:text-light-400 line-clamp-2">
                {chat.message}
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-light-500">
                Channel you are a member of
              </p>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const renderItem = (item) => {
    if (activeFilter === "all") {
      if (item.itemType === "task") {
        return renderTaskCard(item);
      } else if (item.itemType === "ticket") {
        return renderTicketCard(item);
      } else if (item.itemType === "chat") {
        return renderChatCard(item);
      }
    } else if (activeFilter === "tasks") {
      return renderTaskCard(item);
    } else if (activeFilter === "tickets") {
      return renderTicketCard(item);
    } else if (activeFilter === "chat") {
      return renderChatCard(item);
    }
    return null;
  };

  // Get selected employee name for display
  const selectedEmployee = useMemo(() => {
    if (!isAdminUser || !selectedEmployeeId) return employee;
    return (
      employees.find(
        (emp) =>
          emp._id === selectedEmployeeId || emp.id === selectedEmployeeId,
      ) || employee
    );
  }, [isAdminUser, selectedEmployeeId, employees, employee]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchAssignedTasks(),
      fetchAssignedTickets(),
      fetchRelevantChats(),
    ]);
    setLoading(false);
  };

  return (
    <PageWrapper
      title={
        isAdminUser &&
        selectedEmployeeId !== employee?._id &&
        selectedEmployeeId !== employee?.id
          ? `Assigned to ${selectedEmployee?.fullNameEn || selectedEmployee?.fullNameAr || "Employee"}`
          : "Assigned to You"
      }
      subtitle={
        isAdminUser &&
        selectedEmployeeId !== employee?._id &&
        selectedEmployeeId !== employee?.id
          ? `Viewing assignments for ${selectedEmployee?.fullNameEn || selectedEmployee?.fullNameAr || "this employee"}`
          : "Your assigned tasks, tickets & chats in one place."
      }
      actions={
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          className="btn-ghost"
          title="Refresh list"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      }
    >
      {/* Admin: Employee Selector */}
      {isAdminUser && (
        <div className="mb-4">
          <div className="relative w-full sm:w-auto sm:inline-block">
            <button
              onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
              className="w-full sm:w-auto flex items-center gap-2 h-10 px-3.5 bg-panel text-ink rounded-xl border border-line2 hover:bg-hover transition-colors"
            >
              <User className="w-4 h-4" />
              <span className="font-medium flex-1 text-left">
                {selectedEmployee?.fullNameEn ||
                  selectedEmployee?.fullNameAr ||
                  "Select Employee"}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showEmployeeDropdown ? "rotate-180" : ""}`}
              />
            </button>

            {showEmployeeDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowEmployeeDropdown(false)}
                />
                <div className="absolute top-full left-0 mt-2 w-full sm:w-64 bg-panel-solid border border-line2 rounded-2xl shadow-modal z-20 max-h-80 overflow-y-auto">
                  {loadingEmployees ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-light-500">
                      Loading employees...
                    </div>
                  ) : employees.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-light-500">
                      No employees found
                    </div>
                  ) : (
                    <>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentUserId = employee?._id || employee?.id;
                          setSelectedEmployeeId(currentUserId);
                          setShowEmployeeDropdown(false);
                        }}
                        className={`px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors ${
                          !selectedEmployeeId ||
                          String(selectedEmployeeId) ===
                            String(employee?._id) ||
                          String(selectedEmployeeId) === String(employee?.id)
                            ? "bg-accent-soft text-accent-ink font-medium"
                            : ""
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {employee?.fullNameEn || employee?.fullNameAr || "Me"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-light-500">
                          {employee?.employeeCode || ""}
                        </div>
                      </div>
                      <div className="border-t border-gray-200 dark:border-dark-100" />
                      {employees
                        .filter((emp) => {
                          const empId = emp._id || emp.id;
                          const currentUserId = employee?._id || employee?.id;
                          return String(empId) !== String(currentUserId);
                        })
                        .map((emp) => {
                          const empId = emp._id || emp.id;
                          const isSelected =
                            String(selectedEmployeeId) === String(empId);
                          return (
                            <div
                              key={empId}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEmployeeId(empId);
                                setShowEmployeeDropdown(false);
                              }}
                              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors ${
                                isSelected
                                  ? "bg-accent-soft text-accent-ink font-medium"
                                  : ""
                              }`}
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-light-100">
                                {emp.fullNameEn || emp.fullNameAr || "Unknown"}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-light-500">
                                {emp.employeeCode || ""}{" "}
                                {emp.team ? `• ${emp.team}` : ""}
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div role="tablist" className="mb-6 flex gap-1 overflow-x-auto border-b border-line scrollbar-none">
        {filters.map((filter) => {
          const active = activeFilter === filter.id;
          return (
            <button
              key={filter.id}
              role="tab"
              aria-selected={active}
              onClick={() => setActiveFilter(filter.id)}
              className={`relative flex-shrink-0 flex items-center gap-2 px-3.5 h-10 text-[14px] font-medium transition-colors ${
                active ? "text-ink" : "text-muted hover:text-ink"
              }`}
            >
              <span>{filter.label}</span>
              <span
                className={`min-w-[22px] h-5 px-1.5 rounded-full font-mono text-[11px] flex items-center justify-center ${
                  active ? "bg-grad text-on-accent" : "bg-raise text-faint"
                }`}
              >
                {filter.count}
              </span>
              {active && <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-grad" />}
            </button>
          );
        })}
      </div>

      {/* Content Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-accent mx-auto mb-4"></div>
            <p className="text-sm text-gray-500 dark:text-light-500">
              Loading assigned items...
            </p>
          </div>
        </div>
      ) : filteredData.items.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <AnimatePresence mode="wait">
            {filteredData.items.map((item) => (
              <React.Fragment
                key={`${item.itemType || activeFilter}-${item._id || item.id}`}
              >
                {renderItem(item)}
              </React.Fragment>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="card text-center py-12"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-dark-100 flex items-center justify-center">
            {activeFilter === "tasks" ? (
              <ListTodo className="w-8 h-8 text-gray-400 dark:text-light-500" />
            ) : activeFilter === "tickets" ? (
              <Ticket className="w-8 h-8 text-gray-400 dark:text-light-500" />
            ) : activeFilter === "chat" ? (
              <MessageSquare className="w-8 h-8 text-gray-400 dark:text-light-500" />
            ) : (
              <FileText className="w-8 h-8 text-gray-400 dark:text-light-500" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-light-100 mb-2">
            No items found
          </h3>
          <p className="text-sm text-gray-600 dark:text-light-500">
            {activeFilter === "all"
              ? "You don't have any assigned items yet."
              : `You don't have any ${activeFilter} assigned to you yet.`}
          </p>
        </motion.div>
      )}
    </PageWrapper>
  );
};

export default AssignedToYou;
